/* tslint:disable:component-selector import-spacing no-trailing-whitespace no-inferrable-types */
// noinspection TypeScriptUnresolvedVariable

import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy
}                            from '@angular/core';

import { Logger                 } from '@/util/logger';

import { ActionService          } from "@/services/action.service";
import { FullscreenToggleAction } from "@/components/krpano/actions/fullscreen.toggle.action";
import { PanoZoomInAction       } from "@/components/krpano/actions/pano.zoom.in.action";
import { PanoZoomOutAction      } from "@/components/krpano/actions/pano.zoom.out.action";
import { PanoZoomToAction       } from "@/components/krpano/actions/pano.zoom.to.action";
import { PanoLookLeftAction     } from "@/components/krpano/actions/pano.look.left.action";
import { PanoLookRightAction    } from "@/components/krpano/actions/pano.look.right.action";
import { PanoLookUpAction       } from "@/components/krpano/actions/pano.look.up.action";
import { PanoLookDownAction     } from "@/components/krpano/actions/pano.look.down.action";
import { PanoResetAction        } from "@/components/krpano/actions/pano.reset.action";

import { Event                  } from "@/components/krpano/events/event";
import { EventType              } from "@/components/krpano/events/event.type";
import { KeyboardEvent          } from "@/components/krpano/events/keyboard.event";

import { KRPanoramaViewer       } from "@/components/krpano/k.r.panorama.viewer";

import { MatSidenav }             from "@angular/material/sidenav";

import * as $                     from 'jquery';
import KeyUpEvent = JQuery.KeyUpEvent;
import KeyDownEvent = JQuery.KeyDownEvent;
import {PanoNavigateById} from "@/components/krpano/actions/pano.navigate.by.id";
import {PanoramaService} from "@/services/panorama.service";
import {PanoramaSelection} from "@/models/panorama.selection";
import {Panorama} from "@/models/panorama";
import {ActionType} from "@/models/action.type";
import {MapToggleAction} from "@/components/krpano/actions/map.toggle.action";
import {LeafletViewer} from "@/components/leaflet/leaflet.viewer";
import {ViewOrientation} from "@/models/view.orientation";
import {Subscription} from "rxjs";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {NavigateNext} from "@/components/leaflet/actions/navigate.next";
import {NavigateOverwatch} from "@/components/leaflet/actions/navigate.overwatch";
import {NavigatePrevious} from "@/components/leaflet/actions/navigate.previous";
import {NavigateLeft} from "@/components/leaflet/actions/navigate.left";
import {NavigateRight} from "@/components/leaflet/actions/navigate.right";
import {KeyboardActions} from "@/helpers/keyboard.actions";
import {PanoramaControlsComponent} from "@/components/panorama.controls.component";
import {PanoControlsToggle} from "@/components/krpano/actions/pano.controls.toggle";
import {PanoramaSelector} from "@/components/panorama.selector";
import {PanoUnlockApplication} from "@/components/krpano/actions/pano.unlock.application";

@Component({
  standalone: false,
  selector: 'panorama',
  template: `
    <div id="panorama-inner" class="panorama-inner">

      <!-- the actual panoramic viewer (takes up all the background -->
      <panorama-viewer-wrapper (pano_event)="listen($event)"></panorama-viewer-wrapper>

      <!-- the brightness knob -->
      <pano-brightness id="pano-brightness" class="pano-brightness" (brightness_event)="brightness($event)"></pano-brightness>

      <!-- the contrast knob -->
      <pano-contrast id="pano-contrast" class="pano-contrast" (contrast_event)="contrast($event)"></pano-contrast>

      <!-- controls / action bar at the bottom -->
      <pano-controls id="pano-controls" class="pano-controls" (expanded_event)="expanded($event)"></pano-controls>

      <!-- the TV remote style panorama navigator pad -->
      <pano-navigator></pano-navigator>

      <!-- the minimap -->

      <leaflet-viewer-wrapper mode="minimap" class="minimap" [class.disabled]="!enabled()" (pano_jump)="jumpTo($event)"></leaflet-viewer-wrapper>

      <!-- the panorama jump to navigator -->
      <pano-selector></pano-selector>

      <!-- the bubble help we show when hovering over corridor lines or markers -->
      <div id="leaflet-popup-panel" class="leaflet-popup-panel">Popup!</div>

    </div>
  `
})

/**
 *
 * @class PanoramaController
 *
 * the controller that contains the KRPano viewer and related components like the map
 * widget etc.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
export class PanoramaController implements AfterViewInit, OnDestroy {

  @ViewChild(KRPanoramaViewer)
  public         viewer: KRPanoramaViewer;

  @ViewChild(LeafletViewer)
  public         leaflet: LeafletViewer;

  @ViewChild(PanoramaControlsComponent)
  public         controls: PanoramaControlsComponent;

  @ViewChild(PanoramaSelector)
  public         selector: PanoramaSelector;

  /** are we in full screen mode? */
  public fullscreenEnabled: boolean               = false;

  /** the image brightness filter value */
  private _brightness: number                     = 1.0;

  /** hte image contrast filter value */
  private _contrast: number                       = 1.0;

  /** are we showing the map widget right now? */
  private _enabled: boolean                       = true;

  /** our subscription to panorama changes */
  private _panoSubscription: Subscription         = null;

  /** the current panorama we are focused on */
  private _panorama: Panorama                     = null;

  /** the lat to return to on the overwatch page (of the marker we clicked on */
  private _rx: number                             = 0.0;

  /** the lng to return to on the overwatch page (of the marker we clicked on */
  private _ry: number                             = 0.0;

  /** the zoom level to return to on the overwatch page */
  private _rz: number                             = 0.0;

  /** our hot key actions */
  private _keyboard: KeyboardActions              = null;

  /** standard constructor */
  constructor(
    private actions: ActionService,
    private panos:   PanoramaService,
    private router:  Router,
    private route:   ActivatedRoute
  ) {

    Logger.debug('[PanoramaController] ctor...');

    this._keyboard = new KeyboardActions(this);
  }

  ngAfterViewInit() {

    Logger.debug('[PanoramaController] after view init...');

    Logger.debug('[PanoramaController] init...');

    const params: ParamMap    = this.route.snapshot['queryParamMap'];

    if (params.has('rx')) {

      this._rx = Number(params.get('rx'));
    }

    if (params.has('ry')) {

      this._ry = Number(params.get('ry'));
    }

    if (params.has('rz')) {

      this._rz = Number(params.get('rz'));
    }

    /* stop key events from falling through to the browser */

    $('#panorama-inner').on('keyup', (e: KeyUpEvent) => {

      let filterKeys: string[] = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F11', 'Escape'];

      if (filterKeys.findIndex(x => x == e.originalEvent.code) > -1) {

        e.preventDefault();

      }

    });

    $('#panorama-inner').on('keydown', (e : KeyDownEvent) => {

      let filterKeys: string[] = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F11', 'Escape'];

      if (filterKeys.findIndex(x => x == e.originalEvent.code) > -1) {

        e.preventDefault();

      }

    });

    let pc: PanoramaController  = this;

    pc._panoSubscription = pc.panos.onPanorama().subscribe(
      (meta: Panorama) => {

        if (pc._panorama && (pc._panorama.id == meta.id)) {

          Logger.debug(`[PanoramaController] panorama change, but already set, ignoring.`);

          return ;
        }

        Logger.debug(`[PanoramaController] panorama change...`);

        pc._panorama = meta;

      });

    /* register our control actions */

    this.actions.add(new FullscreenToggleAction(this));
    this.actions.add(new PanoZoomInAction(this));
    this.actions.add(new PanoZoomOutAction(this));
    this.actions.add(new PanoZoomToAction(this));
    this.actions.add(new PanoLookLeftAction(this));
    this.actions.add(new PanoLookRightAction(this));
    this.actions.add(new PanoLookUpAction(this));
    this.actions.add(new PanoLookDownAction(this));
    this.actions.add(new PanoResetAction(this));
    this.actions.add(new PanoNavigateById(this));
    this.actions.add(new MapToggleAction(this));

    this.actions.add(new NavigateNext(this));
    this.actions.add(new NavigatePrevious(this));
    this.actions.add(new NavigateLeft(this));
    this.actions.add(new NavigateRight(this));
    this.actions.add(new NavigateOverwatch(this));

    this.actions.add(new PanoUnlockApplication(this));

    this.actions.add(new PanoControlsToggle(this));
  }

  ngOnDestroy() {

    let pc: PanoramaController  = this;

    Logger.debug(`[PanoramaController] ngOnDestroy ...`);

    pc._panoSubscription.unsubscribe();

    /*
     * shouldn't be necessary
     *
    this.panos.refresh(true);
     *
     */
  }

  listen(e: Event) {

    let pc: PanoramaController = this;

    /* watch for keyboard commands */

    if (e instanceof KeyboardEvent) {

      this._keyboard.hotkey(e);
    }

    /*
     * watch for panorama loading completed, this is when we are "ready" to do
     * stuff withe panorama.
     *
     */

    if (e instanceof Event) {

      if (e.type() == EventType.LOAD_COMPLETE) {

        Logger.debug(`[PanoramaController] panorama has loaded!`);

        /* hint to the milepost navigator; this is where we are now */

        let meta: Panorama =  pc.viewer.meta();

        pc.panos.select(new PanoramaSelection({sub: meta.sub, timeline: meta.timeline, mile: meta.mile}));

        pc.panos.refresh(true);
      }
    }

    /*
     * watch for changes to the view in KR Pano, so we can pick up things like the view
     * being rotated or zoomed.  We can then let leaflet know, so that it can track the
     * current viewing cone on the mini-map.
     *
     */

    if (e instanceof Event) {

      if ((e.type() == EventType.VIEW_CHANGE) || (e.type() == EventType.VIEW_CHANGE)) {

        let orientation: ViewOrientation = new ViewOrientation({
          h:   pc.viewer.h(),
          v:   pc.viewer.v(),
          fov: pc.viewer.fov()
        });

        pc.leaflet.viewChanged(orientation);

      }
    }
  }

  /** handle changes in the brightness filter */
  brightness(value: number) {

    Logger.debug('[PanoramaController] brightness: ', value);

    this._brightness = value;

    this.updateFilters();
  }

  /** handle changes in the contrast filter */
  contrast(value: number) {

    Logger.debug('[PanoramaController] contrast: ', value);

    this._contrast = value;

    this.updateFilters();
  }

  updateFilters() {

    $('panorama-viewer-wrapper').css({
      '-webkit-filter': 'contrast(' + this._contrast + ')' + ' brightness(' + this._brightness + ')',
      'filter': 'contrast(' + this._contrast + ')' + ' brightness(' + this._brightness + ')'
    });

  }

  /** react to the toolbar opening/closing */
  expanded(open: boolean) {

    Logger.debug('[PanoramaController] toolbar open: ', open);

    if (open) {

      jQuery('.knob-wrapper').css('visibility', 'visible');

    } else {

      jQuery('.knob-wrapper').css('visibility', 'hidden');
    }
  }

  /** zoom in by relative percentage */
  zoomIn(percentDelta: number) {

    Logger.debug('[PanoramaController] zooming out by: ', percentDelta);

    this.viewer.zoomIn(percentDelta);
  }

  /** zoom out by relative percentage */
  zoomOut(percentDelta: number) {

    Logger.debug('[PanoramaController] zooming out by: ', percentDelta);

    this.viewer.zoomOut(percentDelta);
  }

  /** zoom to absolute percentage */
  zoomTo(percent: number) {

    Logger.debug('[PanoramaController] zooming to: ', percent);

    this.viewer.zoomTo(percent);
  }

  /** look down by delta degrees */
  lookDown(delta: number = 10.0) {

    Logger.debug('[PanoramaController]', 'turning down...');

    this.viewer.lookDown(delta);
  }

  /** look up by delta degrees */
  lookUp(delta: number = 10.0) {

    Logger.debug('[PanoramaController]', 'turning up...');

    this.viewer.lookUp(delta);
  }

  /** look down by delta degrees */
  lookLeft(delta: number = 10.0) {

    Logger.debug('[PanoramaController]', 'turning left...');

    this.viewer.lookLeft(delta);
  }

  /** look down by delta degrees */
  lookRight(delta: number = 10.0) {

    Logger.debug('[PanoramaController]', 'turning right...');

    this.viewer.lookRight(delta);
  }

  unlock() {

    let pc: PanoramaController = this;

    if (!pc.viewer) {

      return ;
    }

    pc.viewer.unlockApplication();
  }

  /** test if we are currently locked; waiting for a panorama load/rendering to be completed. */
  isLocked(): boolean {

    let pc: PanoramaController = this;

    if (!pc.viewer) {

      /* assume we are pending the creation and loading of krpeno */

      return true;
    }

    return pc.viewer.isLocked();
  }

  /** return the viewer to starting position */
  reset() {

    Logger.debug('[PanoramaController]', 'resetting...');

    this.viewer.resetLook();

    this.actions.action(ActionType.BRIGHTNESS_RESET).execute();
    this.actions.action(ActionType.CONTRAST_RESET).execute();
  }

  /** jump to another panorama view */
  navigateTo(id: number) {

    Logger.debug(`[PanoramaController] navigating directly to: `, id);



  }

  /** test if the user is looking out over the tailgate, isntead of forward over the hood */
  isLookingBack(): boolean {

    /*
     * normally the user is looking out "over the hood".  That reference point is always
     * 0 degrees in the panorama viewer.  So, if they've rotated the view to be more than
     * +/- 90 degrees from 0...then they are looking out over the tail gate, not out over
     * the hood.
     *
     * fortunately, the panorama viewer is using normalized spherical co-ordinate angles;
     * so the horizontal swing is with 0 over the hood and to the right is 0-180 (180
     * being directly over the tail gate), and to the left is -0 to -180. so, all we have
     * to do to check for looking backwards is check if the absolute horizontal swing
     * is more than 90 degrees.
     *
     */

    let pc: PanoramaController = this;
    let swing: number          = pc.viewer.h();

    if (Math.abs(swing) > 90) {

      return true;
    }

    /* they are not looking over the tail gate */

    return false;
  }

  /** jump to the next panorama after this one on the track */
  navigateNext() {

    let pc: PanoramaController = this;
    let p: Panorama            = pc._panorama;
    let flipped                = pc.isLookingBack();

    if (flipped) {

      /*
       * orientation is reversed, they are looking over the tail gate so we keep going in that
       * direction.
       *
       */

      if (p && p.previous) {

        Logger.debug(`[PanoramaController] jumping backward-next 'prev': `, p.previous);

        pc.jumpTo(p.previous);
      }

    } else {

      if (p && p.next) {

        Logger.debug(`[PanoramaController] jumping forward 'next': `, p.next);

        pc.jumpTo(p.next);
      }
    }
  }

  /** jump to the next panorama before this one on the track */
  navigatePrevious() {

    let pc: PanoramaController = this;
    let p: Panorama            = pc._panorama;
    let flipped                = pc.isLookingBack();

    if (flipped) {

      if (p && p.next) {

        Logger.debug(`[PanoramaController] jumping forward-prev 'next': `, p.next);

        pc.jumpTo(p.next);
      }

    } else {

      if (p && p.previous) {

        Logger.debug(`[PanoramaController] jumping backward 'previous': `, p.previous);

        pc.jumpTo(p.previous);
      }
    }


  }

  /** jump to the next panorama parallel to this one but a track to the left. */
  navigateLeft() {

    let pc: PanoramaController = this;
    let p: Panorama            = pc._panorama;

    if (p && p.left) {

      Logger.debug(`[PanoramaController] jumping ot 'left': `, p.left);

      pc.jumpTo(p.left);
    }
  }

  /** jump to the next panorama parallel to this one but a track to the right. */
  navigateRight() {

    let pc: PanoramaController = this;
    let p: Panorama            = pc._panorama;

    if (p && p.right) {

      Logger.debug(`[PanoramaController] jumping ot 'right': `, p.right);

      pc.jumpTo(p.right);
    }
  }

  /** jump back to the overwatch screen. */
  navigateOverwatch() {

    let pc: PanoramaController = this;

    Logger.debug(`[PanoramaController] jumping back to overwatch...`);

    this.panos.overwatchNavigate(this._rx, this._ry, this._rz);

    /* when we go back to the overwatch screen, clear the mile post selector */

    this.selector.clear();
  }

  /** tell the KR Pano viewer to jump to this panorama */
  jumpTo(p: Panorama) {

    this.viewer.focus(p.id);
  }

  /** check if we are currently showing the leaflet widget */
  enabled() {

    return this._enabled;
  }

  milePostFocus() {

    this.selector.milePostFocus();
  }

  toggleFilter() {

    this.actions.action(ActionType.PANO_SELECTOR_TOGGLE).execute();
  }

  toggleControls() {

    this.controls.toggleAction();
  }

  /** turn the map widget on/off */
  toggleVisible() {

    if (this._enabled) {

      this._enabled = false;

      return ;
    }

    this._enabled = true;
  }


  /** switch to full screen and back */
  toggleFullscreen() {

    /*
     * Firefox has a bug where sometimes it will deny the request for full screen, if your
     * code in this event handler is taking to long.  To try and avoid that, we don't do any
     * console logging as part of the full screen toggle handling.
     *
     * ref: https://stackoverflow.com/questions/23913111/firefox-browser-rejected-fullscreen-change
     *
     * In our code; we process the events being published via KR Pano, so it might be that
     * sometimes (for whatever reason), it's masking the "user generated" part by the way KR
     * Pano and Angular are interacting with promises...its "detaching" from the original
     * event somehow.
     *
     * We could fix that by watching for F11 key inside KRPano (and shorten the chain), but
     * it's no guarantee (we don't know exactly where it gets detached, or why its only
     * sometimes.  Seems like a timing issue in KR Pano.  So for now...do nothing.  Users
     * should just use Chrome.
     *
     * https://ironroad.atlassian.net/browse/RL-47
     *
     */

    if (!(!window.screenTop && !window.screenY) && this.fullscreenEnabled) {

      this.fullscreenEnabled = false;
    }


    if (this.fullscreenEnabled) {

      if (document.exitFullscreen) {

        document.exitFullscreen();

      }

      this.fullscreenEnabled = false;

    } else {

      const elem = document.getElementById(`panorama-inner`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();

      }

      this.fullscreenEnabled = true;
    }
  }


}
